import React from 'react'
import {
  Avatar,
  Button,
  Card,
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  List,
  Modal,
  Row,
  Select,
  Space,
  Switch,
  Tag,
  Upload
} from 'antd'
import { EditOutlined, UploadOutlined, UserOutlined } from '@ant-design/icons'
import type { UploadFile, FormInstance } from 'antd/es'
import dayjs from 'dayjs'
import type { Vehicle } from '../types'
import { formatPeriodLabel, getExpectedBetween, getVehicleDueDate, type PreviewItem } from './vehicleManagerUtils'

const { Option } = Select

type ExpiringVehiclesModalProps = {
  visible: boolean
  vehicles: Vehicle[]
  onClose: () => void
  getDueDateStatus: (dueDate?: string) => { color: string; text: string }
  onEdit: (vehicle: Vehicle) => void
  canEdit?: boolean
}

export const ExpiringVehiclesModal: React.FC<ExpiringVehiclesModalProps> = ({
  visible,
  vehicles,
  onClose,
  getDueDateStatus,
  onEdit,
  canEdit = true
}) => (
  <Modal
    title={`即将到期车辆 (${vehicles.length})`}
    open={visible}
    onCancel={onClose}
    footer={null}
    width={820}
  >
    <List
      itemLayout="vertical"
      dataSource={vehicles}
      renderItem={(vehicle) => {
        const dueStatus = getDueDateStatus(getVehicleDueDate(vehicle))
        return (
          <List.Item
            key={vehicle._id || vehicle.id}
            actions={[
              <Button key="edit" type="link" icon={<EditOutlined />} onClick={() => { onClose(); onEdit(vehicle) }} disabled={!canEdit}>
                修改/续租
              </Button>
            ]}
          >
            <List.Item.Meta
              avatar={<Avatar style={{ backgroundColor: '#cfa972' }}>{vehicle.plate}</Avatar>}
              title={(
                <Space>
                  <Tag color={dueStatus.color}>{dueStatus.text}</Tag>
                  <span>{vehicle.plate}</span>
                </Space>
              )}
              description={
                <div>
                  <div><strong>公司:</strong> {vehicle.company}</div>
                  <div><strong>联系人:</strong> {vehicle.contactPerson || '-'} {(vehicle as any).contactPerson2 ? `/ ${(vehicle as any).contactPerson2}` : ''}</div>
                  <div><strong>电话:</strong> {vehicle.phone} {(vehicle as any).phone2 ? `/ ${(vehicle as any).phone2}` : ''}</div>
                  <div><strong>租金:</strong> ￥{vehicle.rentAmount || 0}</div>
                  <div><strong>提醒金额:</strong> ￥{vehicle.reminderAmount ?? vehicle.rentAmount ?? 0}</div>
                  <div><strong>提醒周期:</strong> {formatPeriodLabel(vehicle.reminderPeriod || vehicle.rentPeriod, vehicle.reminderPeriodMonths)}</div>
                  <div><strong>押金:</strong> ￥{vehicle.depositAmount ?? '-'}</div>
                  <div><strong>地址:</strong> {vehicle.address || '-'}</div>
                </div>
              }
            />
          </List.Item>
        )
      }}
    />
  </Modal>
)

type RentModalProps = {
  visible: boolean
  onOk: () => void
  onClose: () => void
  form: FormInstance
  mergeByCompany: boolean
  onMergeByCompanyChange: (val: boolean) => void
  onVehicleSelectionChange: (ids: string[]) => void
  selectedVehicles: Vehicle[]
  vehicles: Vehicle[]
  makers: any[]
  getOutstanding: (v: Vehicle) => number
  fileList: UploadFile<any>[]
  onFileListChange: (list: UploadFile<any>[]) => void
}

export const RentModal: React.FC<RentModalProps> = ({
  visible,
  onOk,
  onClose,
  form,
  mergeByCompany,
  onMergeByCompanyChange,
  onVehicleSelectionChange,
  selectedVehicles,
  vehicles,
  makers,
  getOutstanding,
  fileList,
  onFileListChange
}) => {
  const watchedItems = Form.useWatch('items', form)
  const totalActual = Array.isArray(watchedItems)
    ? watchedItems.reduce((sum: number, item: any) => sum + Number(item?.actualAmount || 0), 0)
    : 0


  React.useEffect(() => {
    if (!watchedItems) return
    console.log('[RentModal] items snapshot', watchedItems)
    if (!mergeByCompany) return
    const sum = watchedItems.reduce((acc: number, item: any) => acc + Number(item?.amount || 0), 0)
    const currentTotal = Number(form.getFieldValue('totalAmount') || 0)
    if (Math.abs(sum - currentTotal) > 0.01) {
      form.setFieldsValue({ totalAmount: sum })
    }
  }, [watchedItems])

  const normalizeVehicleKey = (val: any) => String(val ?? '').trim().toUpperCase()

  const matchesVehicleId = (vehicle: Vehicle, vehicleId: any) => {
    const target = normalizeVehicleKey(vehicleId)
    if (!target) return false
    const keys = [
      vehicle._id,
      vehicle.id,
      vehicle.plate,
      (vehicle as any).forklift_no
    ].map(normalizeVehicleKey)
    return keys.includes(target)
  }

  return (
  <Modal
    title="批量收款"
    open={visible}
    onOk={onOk}
    onCancel={onClose}
    width={720}
  >
    <Form form={form} layout="vertical" preserve={false}>
      <Form.Item
        name="vehicleIds"
        label="选择车辆"
        rules={[{ required: true, message: '请选择车辆' }]}
      >
        <Select
          placeholder="选择需要收款的车辆"
          showSearch
          mode="multiple"
          optionFilterProp="children"
          onChange={onVehicleSelectionChange}
          value={form.getFieldValue('vehicleIds')}
        >
          {vehicles.map((v, idx) => {
            const key = v._id || v.id || v.plate || `veh-${idx}`
            const value = String(v._id || v.id || v.plate || key)
            return (
              <Option key={key} value={value} disabled={Boolean((v as any).returnSigned)}>
                {v.plate} - {v.company}
              </Option>
            )
          })}
        </Select>
      </Form.Item>
      <Form.Item
        name="makerId"
        label="制单人"
        rules={[{ required: true, message: '请选择制单人' }]}
      >
        <Select placeholder="选择制单人">
          {makers.map((m: any) => {
            const id = String(m._id || m.id || m.username || m.nickName)
            return (
              <Option key={id} value={id}>
                {m.nickName || m.username || id}
              </Option>
            )
          })}
        </Select>
      </Form.Item>
      <Row gutter={12}>
        <Col span={12}>
          <Form.Item label="按公司合并收款" name="mergeByCompany" valuePropName="checked">
            <Switch
              checked={mergeByCompany}
              onChange={(val) => {
                onMergeByCompanyChange(val)
                if (val) {
                  form.setFieldsValue({ totalAmount: undefined })
                }
              }}
            />
          </Form.Item>
        </Col>
        {mergeByCompany && (
          <Col span={12}>
            <Form.Item
              label="收款总额"
              name="totalAmount"
              rules={[{ required: true, message: '请输入收款总额' }]}
            >
              <InputNumber style={{ width: '100%' }} min={0} placeholder="本次总收款金额" />
            </Form.Item>
          </Col>
        )}
      </Row>
      <Form.List name="items">
        {(fields) => (
          <>
            {fields.map((field) => {
              const { key, ...restField } = field
              const vehicleId = form.getFieldValue(['items', field.name, 'vehicleId'])
              const vehicle =
                selectedVehicles.find(v => matchesVehicleId(v, vehicleId)) ||
                vehicles.find(v => matchesVehicleId(v, vehicleId))
              const outstanding = vehicle ? getOutstanding(vehicle) : 0
              const range = form.getFieldValue(['items', restField.name, 'chargeRange'])
              const rangeStart = range?.[0]
              const rangeEnd = range?.[1]
              const rangeExpected = vehicle && rangeStart && rangeEnd
                ? getExpectedBetween(vehicle, rangeStart.format('YYYY-MM-DD'), rangeEnd.format('YYYY-MM-DD'))
                : null
              const tagValue = rangeExpected !== null ? rangeExpected : outstanding
              const outstandingLabel = rangeExpected !== null
                ? `区间应收 ￥${Number(rangeExpected).toFixed(2)}`
                : `待收 ￥${Number(outstanding).toFixed(2)}`
              if (!vehicle) {
                console.warn('[RentModal] missing vehicle for item', {
                  vehicleId,
                  fieldName: field.name,
                  selectedIds: (selectedVehicles || []).map(v => String(v._id || v.id || v.plate)),
                  allIds: (vehicles || []).map(v => String(v._id || v.id || v.plate))
                })
              }
              return (
                <Card key={key} size="small" style={{ marginBottom: 12 }}>
                  <Row gutter={12}>
                    <Col span={12}>
                      <div><strong>{vehicle?.plate || vehicleId}</strong> {vehicle?.company ? `- ${vehicle.company}` : ''}</div>
                      <div style={{ color: '#999', fontSize: 12 }}>
                        联系人: {vehicle?.contactPerson || '-'} / {vehicle?.phone || '-'}
                      </div>
                    </Col>
                    <Col span={12} style={{ textAlign: 'right' }}>
                      <Tag color={tagValue > 0 ? 'red' : 'green'}>{outstandingLabel}</Tag>
                    </Col>
                  </Row>
                  <Row gutter={12} style={{ marginTop: 8 }}>
                    <Col span={24}>
                      <Form.Item
                        {...restField}
                        name={[restField.name, 'chargeRange']}
                        label="本次扣款范围"
                      >
                        <DatePicker.RangePicker
                          style={{ width: '100%' }}
                          onChange={(range) => {
                            const start = range?.[0]
                            const end = range?.[1]
                            if (!vehicle || !start || !end) return
                            const amount = getExpectedBetween(
                              vehicle,
                              start.format('YYYY-MM-DD'),
                              end.format('YYYY-MM-DD')
                            )
                            form.setFields([{
                              name: ['items', restField.name, 'amount'],
                              value: Number(amount.toFixed(2))
                            }])
                          }}
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row gutter={12} style={{ marginTop: 8 }}>
                    <Col span={12}>
                      <Form.Item
                        {...restField}
                        name={[restField.name, 'amount']}
                        label="本次收款金额"
                        rules={mergeByCompany ? [] : [{ required: true, message: '请输入金额' }]}
                      >
                        <InputNumber
                          style={{ width: '100%' }}
                          min={0}
                          placeholder={mergeByCompany ? '自动分配' : '金额'}
                          disabled={mergeByCompany}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        {...restField}
                        name={[restField.name, 'actualAmount']}
                        label="实际收入"
                      >
                        <InputNumber style={{ width: '100%' }} min={0} placeholder="本车实际收入" />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Form.Item hidden {...restField} name={[restField.name, 'vehicleId']} />
                </Card>
              )
            })}
          </>
        )}
      </Form.List>

      <Form.Item name="notes" label="备注">
        <Input.TextArea rows={3} placeholder="收款备注" />
      </Form.Item>

      <Row gutter={16}>
        <Col span={12}>
          <Form.Item label="收款附件/凭证">
            <Upload
              listType="picture"
              fileList={fileList}
              beforeUpload={() => false}
              onChange={({ fileList: list }) => onFileListChange(list)}
            >
              <Button icon={<UploadOutlined />}>上传图片/PDF</Button>
            </Upload>
          </Form.Item>
        </Col>
        <Col span={12}>
          <div style={{ paddingTop: 4 }}>
            <div style={{ color: '#999', marginBottom: 6 }}>实际收入合计</div>
            <div style={{ fontSize: 18, fontWeight: 600 }}>￥{Number(totalActual || 0).toFixed(2)}</div>
          </div>
        </Col>
      </Row>
    </Form>
  </Modal>
  )
}

type PreviewModalProps = {
  visible: boolean
  onClose: () => void
  onConfirm: () => void
  previewItems: PreviewItem[]
  fileList: UploadFile<any>[]
}

export const PreviewModal: React.FC<PreviewModalProps> = ({
  visible,
  onClose,
  onConfirm,
  previewItems,
  fileList
}) => (
  <Modal
    title="收款确认"
    open={visible}
    onCancel={onClose}
    footer={null}
    width={760}
  >
    {previewItems.length > 0 && (
      <div style={{ lineHeight: 1.6 }}>
        {previewItems.map((item, idx) => (
          <Card key={idx} size="small" style={{ marginBottom: 12 }}>
            <Row gutter={12}>
              <Col span={12}><strong>{item.vehicle.plate}</strong></Col>
              <Col span={12} style={{ textAlign: 'right' }}>
                {item.chargeRangeStart && item.chargeRangeEnd ? (
                  <Tag color={(getExpectedBetween(item.vehicle, item.chargeRangeStart, item.chargeRangeEnd) > 0) ? 'red' : 'green'}>
                    区间应收 ￥{Number(getExpectedBetween(item.vehicle, item.chargeRangeStart, item.chargeRangeEnd)).toFixed(2)}
                  </Tag>
                ) : (
                  <Tag color={item.outstanding > 0 ? 'red' : 'green'}>待收 ￥{Number(item.outstanding).toFixed(2)}</Tag>
                )}
              </Col>
            </Row>
            <div>公司:{item.vehicle.company}</div>
            <div>联系人:{item.vehicle.contactPerson || '-'} / {item.vehicle.phone || '-'}</div>
            <div>本次收款: ￥{item.amount}</div>
            <div>实际收入: ￥{Number(item.actualAmount || 0).toFixed(2)}</div>
            {item.chargeRangeStart && item.chargeRangeEnd ? (
              <div>收款日期范围:{item.chargeRangeStart} ~ {item.chargeRangeEnd}</div>
            ) : null}
          </Card>
        ))}

        <div style={{ textAlign: 'right', marginTop: 8, fontWeight: 600 }}>
          实际收入合计 ￥{previewItems.reduce((sum, item) => sum + Number(item.actualAmount || 0), 0).toFixed(2)}
        </div>


        {fileList.length > 0 && (
          <div style={{ marginTop: 12 }}>
            <div style={{ marginBottom: 8 }}>附件</div>
            <Space wrap>
              {fileList.map((file, idx) => (
                <img
                  key={idx}
                  src={(file as any).thumbUrl || (file as any).url}
                  alt="附件"
                  style={{ width: 160, height: 120, objectFit: 'cover', borderRadius: 4, border: '1px solid #eee' }}
                />
              ))}
            </Space>
          </div>
        )}
      </div>
    )}

    <div style={{ textAlign: 'right', marginTop: 16 }}>
      <Space>
        <Button onClick={onClose}>返回修改</Button>
        <Button type="primary" onClick={onConfirm}>确认提交</Button>
      </Space>
    </div>
  </Modal>
)


